import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Typography, Button, Steps } from 'antd';
import {
  CameraOutlined,
  AudioOutlined,
  DesktopOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useAIAssessmentContext } from '../../../../Context/AIAssessmentContext/AIAssessmentContext';
import { useAppContext } from '../../../../Context/AppContext/AppContext';
import useBrowserDetection from './hooks/useBrowserDetection';
import { useWizardCameraObstruction } from './hooks/useWizardCameraObstruction';
import useAiAssessmentStart from '../../hooks/useAiAssessmentStart';
import CameraSetupStep from './steps/CameraSetupStep';
import MicrophoneSetupStep from './steps/MicrophoneSetupStep';
import ScreenShareStep from './steps/ScreenShareStep';
import './DeviceSetupWizard.scss';

export type SetupStep = 'camera' | 'microphone' | 'screen';

interface DeviceSetupWizardProps {
  onComplete: () => void;
  onCancel: () => void;
}

const STEPS: { key: SetupStep; title: string; icon: React.ReactNode }[] = [
  { key: 'camera', title: 'Camera', icon: <CameraOutlined /> },
  { key: 'microphone', title: 'Microphone', icon: <AudioOutlined /> },
  { key: 'screen', title: 'Screen Share', icon: <DesktopOutlined /> },
];

const DeviceSetupWizard: React.FC<DeviceSetupWizardProps> = ({
  onComplete,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState<SetupStep>('camera');
  const [completedSteps, setCompletedSteps] = useState<Set<SetupStep>>(new Set());
  const [showHelp, setShowHelp] = useState(false);

  // Refs for media streams
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);

  const { organization } = useAppContext();
  const { showPermissionConfirmationModal, setShowPermissionConfirmationModal, displayStreamRef, cameraMicrophoneStreamRef } = useAIAssessmentContext();
  const browserInfo = useBrowserDetection();
  const { handleStartInterviewConfirm } = useAiAssessmentStart();

  // Continuous camera obstruction check when user is on microphone/screen step (camera already completed)
  const cameraMonitorEnabled = currentStep !== 'camera' && completedSteps.has('camera');
  useWizardCameraObstruction({
    streamRef: cameraStreamRef,
    enabled: cameraMonitorEnabled,
    onObstructed: () => handleStepInvalidate('camera'),
  });

  const themeColor = organization?.theme?.primaryColor || '#1890ff';

  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep);

  const handleStepComplete = useCallback((step: SetupStep) => {
    setCompletedSteps((prev) => new Set(prev).add(step));
  }, []);

  // Handle when a step becomes invalid (e.g., camera blocked after verification)
  const handleStepInvalidate = useCallback((step: SetupStep) => {
    setCompletedSteps((prev) => {
      const newSet = new Set(prev);
      newSet.delete(step);
      return newSet;
    });
    // Navigate back to the invalid step if we've moved past it
    const invalidStepIndex = STEPS.findIndex((s) => s.key === step);
    const currentIndex = STEPS.findIndex((s) => s.key === currentStep);
    if (currentIndex > invalidStepIndex) {
      setCurrentStep(step);
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    const currentIndex = STEPS.findIndex((s) => s.key === currentStep);
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1].key);
    }
  }, [currentStep]);

  const handlePrevious = useCallback(() => {
    const currentIndex = STEPS.findIndex((s) => s.key === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].key);
    }
  }, [currentStep]);

  const handleCameraStreamReady = useCallback((stream: MediaStream) => {
    cameraStreamRef.current = stream;
  }, []);

  const handleScreenStreamReady = useCallback((stream: MediaStream) => {
    screenStreamRef.current = stream;
  }, []);

  // Stop and release camera + screen streams (used by handleClose and unmount cleanup)
  const stopAllStreams = useCallback(() => {
    console.log("[Step 7] DeviceSetupWizard: stopAllStreams called (unmount or close)");
    if (cameraStreamRef.current) {
      console.log("[Step 8] DeviceSetupWizard: stopping camera stream tracks (ref was still set)");
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    } else {
      console.log("[Step 8] DeviceSetupWizard: cameraStreamRef is null, skipping camera stop (stream transferred to context)");
    }
    if (screenStreamRef.current) {
      console.log("[Step 9] DeviceSetupWizard: stopping screen stream tracks (ref was still set)");
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    } else {
      console.log("[Step 9] DeviceSetupWizard: screenStreamRef is null, skipping screen stop (stream transferred to context)");
    }
  }, []);

  // Cleanup streams when user closes/cancels the modal
  const handleClose = useCallback(() => {
    stopAllStreams();
    onCancel();
  }, [onCancel, stopAllStreams]);

  // When the wizard unmounts (modal closed by any path), ensure camera/screen are stopped
  useEffect(() => {
    return () => {
      stopAllStreams();
    };
  }, [stopAllStreams]);

  // Handle final completion - directly start the interview
  const handleFinalComplete = useCallback(async () => {
    console.log("[Step 1] DeviceSetupWizard: handleFinalComplete started (Start AI Assessment clicked)");

    // Pass the streams to the context so they can be reused
    if (cameraStreamRef.current && cameraMicrophoneStreamRef) {
      cameraMicrophoneStreamRef.current = cameraStreamRef.current;
      console.log("[Step 2] DeviceSetupWizard: camera stream copied to context", {
        trackCount: cameraStreamRef.current.getTracks().length,
        videoTracks: cameraStreamRef.current.getVideoTracks().length,
        audioTracks: cameraStreamRef.current.getAudioTracks().length,
      });
    } else {
      console.log("[Step 2] DeviceSetupWizard: no camera stream to copy", { hasRef: !!cameraStreamRef.current });
    }
    if (screenStreamRef.current && displayStreamRef) {
      displayStreamRef.current = screenStreamRef.current;
      console.log("[Step 3] DeviceSetupWizard: screen stream copied to context", {
        trackCount: screenStreamRef.current.getTracks().length,
        videoTracks: screenStreamRef.current.getVideoTracks().length,
        audioTracks: screenStreamRef.current.getAudioTracks().length,
      });
    } else {
      console.log("[Step 3] DeviceSetupWizard: no screen stream to copy", { hasRef: !!screenStreamRef.current });
    }

    // Clear wizard refs so unmount cleanup won't stop the shared streams (context now owns them)
    cameraStreamRef.current = null;
    screenStreamRef.current = null;
    console.log("[Step 4] DeviceSetupWizard: cleared wizard refs so cleanup will not stop context streams");

    // Close the device setup wizard modal
    setShowPermissionConfirmationModal(false);
    console.log("[Step 5] DeviceSetupWizard: modal closing (setShowPermissionConfirmationModal false)");

    // Directly start the AI assessment (skip StartAIAssessment screen)
    console.log("[Step 6] DeviceSetupWizard: calling handleStartInterviewConfirm()");
    await handleStartInterviewConfirm();
    console.log("[Step 6b] DeviceSetupWizard: handleStartInterviewConfirm() returned");
  }, [cameraMicrophoneStreamRef, displayStreamRef, setShowPermissionConfirmationModal, handleStartInterviewConfirm]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 'camera':
        return (
          <CameraSetupStep
            onComplete={() => handleStepComplete('camera')}
            onInvalidate={() => handleStepInvalidate('camera')}
            onStreamReady={handleCameraStreamReady}
            isCompleted={completedSteps.has('camera')}
            browserInfo={browserInfo}
          />
        );
      case 'microphone':
        return (
          <MicrophoneSetupStep
            onComplete={() => handleStepComplete('microphone')}
            cameraStream={cameraStreamRef.current}
            isCompleted={completedSteps.has('microphone')}
            browserInfo={browserInfo}
          />
        );
      case 'screen':
        return (
          <ScreenShareStep
            onComplete={() => handleStepComplete('screen')}
            onStreamReady={handleScreenStreamReady}
            isCompleted={completedSteps.has('screen')}
            browserInfo={browserInfo}
          />
        );
      default:
        return null;
    }
  };

  const isLastStep = currentStep === 'screen';
  const canProceed = completedSteps.has(currentStep);
  const isFirstStep = currentStep === 'camera';
  const showStartOnScreen = isLastStep && canProceed;

  if (!showPermissionConfirmationModal) {
    return null;
  }

  return createPortal(
    <div className="device-setup-wizard-overlay">
      <div className="device-setup-wizard">
        {/* Header */}
        <header className="wizard-header">
          <div className="header-left">
            {organization?.logo && (
              <img
                src={organization.logo}
                alt={organization.orgName}
                className="org-logo"
              />
            )}
            <Typography.Title level={4} className="wizard-title">
              Device Setup
            </Typography.Title>
          </div>

          <div className="header-center">
            <Steps
              current={currentStepIndex}
              size="small"
              className="wizard-steps"
              items={STEPS.map((step, index) => ({
                title: step.title,
                icon: step.icon,
                status:
                  completedSteps.has(step.key)
                    ? 'finish'
                    : index === currentStepIndex
                    ? 'process'
                    : 'wait',
              }))}
            />
          </div>

          <div className="header-right">
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={handleClose}
              className="close-btn"
            />
          </div>
        </header>

        {/* Progress Bar */}
        <div
          className="wizard-progress-bar"
          style={{
            background: `linear-gradient(to right, ${themeColor} ${((currentStepIndex + 1) / STEPS.length) * 100}%, #f0f0f0 0%)`,
          }}
        />

        {/* Main Content */}
        <main className="wizard-content">
          <div className="step-container">{renderStepContent()}</div>
        </main>

        {/* Footer */}
        <footer className="wizard-footer">
          <div className="footer-left">
            <Typography.Text type="secondary">
              Step {currentStepIndex + 1} of {STEPS.length}
            </Typography.Text>
          </div>

          <div className="footer-right">
            {!isFirstStep && (
              <Button onClick={handlePrevious} size="large">
                ← Previous
              </Button>
            )}
            
            {!showStartOnScreen ? (
              <Button
                type="primary"
                onClick={handleNext}
                disabled={!canProceed}
                size="large"
                style={{
                  backgroundColor: canProceed ? themeColor : undefined,
                  borderColor: canProceed ? themeColor : undefined,
                }}
              >
                Continue →
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={handleFinalComplete}
                size="large"
                style={{
                  backgroundColor: themeColor,
                  borderColor: themeColor,
                }}
              >
                Start AI Assessment
              </Button>
            )}
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default DeviceSetupWizard;
